import type { FC } from 'react';
import React, { useCallback, useRef, useState } from 'react';
import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';

import { DialogModal } from 'Components/shared/DialogModal';
import { useSeenFilter } from 'Components/VehicleList/SeenFilter';
import useVehicleFilterStore, {
	useFilters,
	useResetFilters,
	useVehicleCount,
} from 'Stores/FilterStore/VehicleListFilter/VehicleListFilterStore';
import { formatText } from 'Utilities/formatters';
import type { ViewingFilterValue } from 'Utilities/seenVehicles/seenVehicles';

import texts from '../../VehicleListV2.texts.json';
import { useFilterDraft } from '../hooks/useFilterDraft';
import { useScrollToStartOfStickyBar } from '../hooks/useStickyFilterBar';

import { AllFiltersContent } from './AllFiltersContent';

import styles from './AllFiltersModal.module.scss';

const LocalT = new MDText(texts.allFiltersModal);

interface AllFiltersModalProps {
	isLoading?: boolean;
	isOpen: boolean;
	onClose: () => void;
}

export const AllFiltersModal: FC<AllFiltersModalProps> = ({ isLoading = false, isOpen, onClose }) => {
	const storeFilters = useFilters();
	const storeSetValue = useVehicleFilterStore((store) => store.setValue);
	const storeUnsetValue = useVehicleFilterStore((store) => store.unsetValue);
	const resetFilters = useResetFilters();
	const vehicleCount = useVehicleCount();
	const [seenFilter, setSeenFilter] = useSeenFilter();
	const scrollToFilterBar = useScrollToStartOfStickyBar();

	// Seen-filter changes are staged separately — they live outside the filter
	// store and are only committed when the user hits Apply.
	const pendingSeenFilterRef = useRef<Nullable<ViewingFilterValue>>(null);
	const [hasSeenFilterChanged, setHasSeenFilterChanged] = useState(false);

	const { count, hasActiveFilters, isCountLoading, localFilters, setValue, unsetValue } = useFilterDraft({
		initial: storeFilters,
		initialCount: vehicleCount,
	});

	const closeWithScroll = useCallback(() => {
		scrollToFilterBar();
		const targetScrollY = window.scrollY;
		onClose();
		requestAnimationFrame(() => {
			window.scrollTo({ behavior: 'instant' as ScrollBehavior, top: targetScrollY });
		});
	}, [onClose, scrollToFilterBar]);

	const handleApply = useCallback(() => {
		for (const [key, value] of Object.entries(localFilters)) {
			if (value == null) {
				storeUnsetValue({ id: key });
			} else {
				storeSetValue({ id: key, value });
			}
		}
		if (pendingSeenFilterRef.current != null) {
			setSeenFilter(pendingSeenFilterRef.current);
		}
		closeWithScroll();
	}, [closeWithScroll, localFilters, setSeenFilter, storeSetValue, storeUnsetValue]);

	const handleClearAll = useCallback(() => {
		resetFilters();
		closeWithScroll();
	}, [closeWithScroll, resetFilters]);

	const handleSeenFilterChange = useCallback(
		(value: ViewingFilterValue) => {
			pendingSeenFilterRef.current = value;
			setHasSeenFilterChanged(value !== seenFilter);
		},
		[seenFilter],
	);

	return (
		<DialogModal
			classNames={{ content: styles.modalContent }}
			footer={
				<div className={styles.footerActions}>
					<div className={styles.clearAllWrapper}>
						<Button
							fullWidth
							disabled={!hasActiveFilters}
							label={LocalT.translate('clearAll')}
							onClick={handleClearAll}
							size="medium"
							variant="secondary"
						/>
					</div>
					<div className={styles.showVehiclesWrapper}>
						<Button
							fullWidth
							label={
								hasSeenFilterChanged || count === null
									? LocalT.translate('showVehiclesNoCount')
									: LocalT.translate('showVehicles', { count: formatText(count) })
							}
							loading={isLoading || isCountLoading}
							onClick={handleApply}
							size="medium"
							variant="primary"
						/>
					</div>
				</div>
			}
			isOpen={isOpen}
			onClose={onClose}
			size="small"
			title={LocalT.translate('title')}
		>
			{isOpen && (
				<AllFiltersContent
					filters={localFilters}
					onSeenFilterChange={handleSeenFilterChange}
					setValue={setValue}
					unsetValue={unsetValue}
				/>
			)}
		</DialogModal>
	);
};
